import { MobileMenuProps } from "./types";
import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, X, Users, ShoppingBag, Sparkles } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Logo } from "@/assets/icons";
import ContactUsButton from "@/components/ui/contactButton";
import { NAV_ITEMS } from "./data";

const MobileMenu = ({
  isOpen,
  onClose,
}: MobileMenuProps): React.JSX.Element => {
  const pathname = usePathname();
  const [isProductsOpen, setIsProductsOpen] = useState(false);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      // Reset submenu when closing
      setIsProductsOpen(false);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-[9998] transition-opacity duration-300 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      <div
        className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white z-[9999] shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <Link
            onClick={onClose}
            href="/"
            className="flex items-center gap-2 font-bold text-[20px] text-[#373A74]"
          >
            <Logo className="w-8 h-8" />
            Trust Vector
          </Link>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
            aria-label="Close menu"
          >
            <X size={20} className="text-slate-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <nav className="flex flex-col py-3">
            {NAV_ITEMS.map((item) => {
              const hasDropdown = Boolean(item.children && item.children.length > 0);

              if (hasDropdown) {
                const isParentActive =
                  pathname === item.href ||
                  item.children?.some((child) => pathname.startsWith(child.href));

                return (
                  <div key={item.label} className="border-b border-slate-100/60 last:border-b-0">
                    <button
                      onClick={() => setIsProductsOpen(!isProductsOpen)}
                      className={`w-full flex items-center justify-between px-5 py-3.5 font-medium transition-colors cursor-pointer ${
                        isParentActive
                          ? "text-[#056686] font-semibold"
                          : "text-[#373A74] hover:bg-slate-50"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {item.label}
                        {isParentActive && (
                          <span className="w-2 h-2 rounded-full bg-[#8ABA28]" />
                        )}
                      </span>
                      {isProductsOpen ? (
                        <ChevronUp size={18} className="text-slate-400" />
                      ) : (
                        <ChevronDown size={18} className="text-slate-400" />
                      )}
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out bg-slate-50/80 ${
                        isProductsOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="py-2 px-3 flex flex-col gap-1.5">
                        {item.children?.map((child) => {
                          const isChildActive = pathname.startsWith(child.href);
                          const isMembaly = child.href.includes("membaly");
                          const isTimeless = child.href.includes("timeless");

                          return (
                            <Link
                              key={child.label}
                              href={child.href}
                              onClick={() => {
                                onClose();
                                setIsProductsOpen(false);
                              }}
                              className={`flex items-start gap-3 p-3 rounded-xl transition-all ${
                                isChildActive
                                  ? "bg-white shadow-xs border border-slate-200 text-[#056686]"
                                  : "hover:bg-white text-[#373A74]"
                              }`}
                            >
                              <div
                                className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 shadow-xs ${
                                  isMembaly
                                    ? "bg-gradient-to-br from-[#8ABA28] to-[#056686] text-white"
                                    : isTimeless
                                    ? "bg-gradient-to-br from-[#056686] to-[#2a9d6b] text-white"
                                    : "bg-gradient-to-br from-[#056686]/20 to-[#8ABA28]/20 text-[#056686]"
                                }`}
                              >
                                {isMembaly ? (
                                  <Users size={17} strokeWidth={2.3} />
                                ) : isTimeless ? (
                                  <ShoppingBag size={17} strokeWidth={2.3} />
                                ) : (
                                  <Sparkles size={17} strokeWidth={2.3} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold text-[14px]">
                                    {child.label}
                                  </span>
                                  {child.badge && (
                                    <span
                                      className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                        isMembaly
                                          ? "bg-[#8ABA28]/15 text-[#486b16]"
                                          : isTimeless
                                          ? "bg-[#056686]/10 text-[#056686]"
                                          : "bg-slate-200 text-slate-600"
                                      }`}
                                    >
                                      {child.badge}
                                    </span>
                                  )}
                                </div>
                                {child.description && (
                                  <p className="text-[12px] text-slate-500 mt-0.5 leading-snug line-clamp-2">
                                    {child.description}
                                  </p>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              const isCurrentActive =
                item.href === "/"
                  ? pathname === "/"
                  : item.href
                  ? pathname.startsWith(item.href)
                  : false;

              return (
                <Link
                  key={item.label}
                  href={item.href ?? ""}
                  onClick={onClose}
                  className={`px-5 py-3.5 font-medium hover:bg-slate-50 transition-colors ${
                    isCurrentActive
                      ? "text-[#056686] font-semibold bg-slate-50/50"
                      : "text-[#373A74]"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="px-5 py-5 border-t border-slate-100" onClick={onClose}>
          <ContactUsButton />
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
